import { useEffect } from "react";
import {
  DEFAULT_DESCRIPTION,
  getCanonical,
  getOrganizationSchema,
  getWebPageSchema,
  SITE_NAME,
  SITE_URL,
} from "@/seo/site";

const upsertMeta = (attribute, key, content) => {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

export const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  pathname = "/",
  robots = "index, follow",
  image = `${SITE_URL}/logo.png`,
  schema,
}) => {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonical = getCanonical(pathname);
    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", robots);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", image);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
    upsertLink("canonical", canonical);

    const schemaId = "risi-seo-jsonld";
    document.getElementById(schemaId)?.remove();
    const pageSchema = getWebPageSchema({ title: fullTitle, description, pathname });
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: SITE_NAME,
      publisher: { "@id": `${SITE_URL}/#organization` },
    };
    const schemas = [getOrganizationSchema(), websiteSchema, pageSchema, ...(schema ? [schema] : [])];
    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);

    return () => document.getElementById(schemaId)?.remove();
  }, [description, image, pathname, robots, schema, title]);

  return null;
};
